import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/nav/Navbar';
import LoadingScreen from '../components/common/LoadingScreen';
import ErrorState from '../components/common/ErrorState';
import EmptyState from '../components/common/EmptyState';
import { useLabel } from '../hooks/useLabel';

const LabelPage = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { label, artists, loading, error } = useLabel(slug);

  if (loading) return <LoadingScreen message="Loading Label..." />;
  if (error || !label) return <ErrorState message={error || 'Label not found'} />;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Navbar />
      <div className="pt-20">
        <header className="relative h-[50vh] overflow-hidden">
          {label.cover_image ? (
            <img src={label.cover_image} alt={label.name}
              className="w-full h-full object-cover opacity-30 grayscale" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-zinc-900 to-zinc-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
          <div className="absolute bottom-12 left-8 md:left-16 space-y-4">
            <span className="px-4 py-1.5 bg-white text-black text-xs font-black tracking-widest">LABEL</span>
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-none text-white">{label.name}</h1>
            {label.founded_year && (
              <p className="text-zinc-400 font-black text-sm uppercase tracking-widest">Est. {label.founded_year}</p>
            )}
          </div>
        </header>

        <main className="max-w-7xl mx-auto py-16 px-8 space-y-16">
          {label.bio && (
            <p className="text-zinc-300 text-xl font-bold leading-relaxed max-w-3xl">{label.bio}</p>
          )}

          <section className="space-y-8">
            <div className="flex items-end justify-between border-b border-zinc-900 pb-6">
              <div className="space-y-2">
                <span className="text-xs font-black tracking-widest text-zinc-500 uppercase">Signed Artists</span>
                <h2 className="text-4xl font-black tracking-tighter text-white uppercase">Roster</h2>
              </div>
              <span className="text-zinc-600 font-black text-sm">{artists.length} artists</span>
            </div>

            {artists.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {artists.map(a => (
                  <Link key={a.id} to={`/${a.slug}`}
                    className="group block bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden hover:border-zinc-600 transition-all hover:-translate-y-1">
                    <div className="aspect-square bg-zinc-900 overflow-hidden">
                      {a.cover_image ? (
                        <img src={a.cover_image} alt={a.name}
                          className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl font-black text-zinc-800">
                          {a.name?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="p-5 space-y-1">
                      <p className="font-black text-white uppercase tracking-tight truncate">{a.name}</p>
                      <p className="text-zinc-500 text-xs font-black uppercase tracking-widest">{a.genre || 'Artist'}</p>
                      <p className="text-zinc-600 text-xs font-bold">{(a.supporter_count || 0).toLocaleString()} supporters</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState title="No artists on roster yet" subtitle="Artists will appear here once they're added to this label." />
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default LabelPage;